// Card decks and hands (issue 1177): the "Cards" tool. Everyone: each deck on the table — how many cards are left to
// draw (never which), the discard pile's top card and count, the cards face up on the table, everyone else's hand as a
// count, and your own hand whole (Play, Discard, Pass to…, Show). The DM (a co-DM with `notes`): their own decks (New
// deck, Edit, Delete; the standard deck as a starter), Put on table, and per deck Deal to…, Deal face up, Shuffle,
// Deal initiative (one card face up to each combatant, the turn order by the cards) and Remove. Nothing here is worked
// out from a card the server did not send: a deck's order is never in the store.
import { useCallback, useEffect, useState } from 'react';
import { CARD_LIMITS, memberLabel, type Card, type DeckSpec, type SeenDeck, type UserDeck } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useMayAct } from '../../table/mayAct';
import { useShell } from '../context';
import { cardCount, deleteDeck, myDecks, placeDeck, saveDeck } from '../../cards';
import { HandoutPicture } from './HandoutsPanel';
import { BTN } from '../ui';

const btn = `${BTN} shrink-0`;
const input = 'w-full rounded border border-stone-700 bg-stone-900 px-2 py-1 text-sm';

/** A deck's cards as the editor writes them: one per line, "label" or "label = rank". */
export function cardsFromLines(text: string): DeckSpec['cards'] {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const m = /^(.*?)\s*=\s*(-?\d+)$/.exec(line);
    return m && m[1] ? { label: m[1].trim(), rank: Number(m[2]) } : { label: line };
  });
}
export const linesFromCards = (cards: DeckSpec['cards']): string => cards.map((c) => (c.rank === undefined ? c.label : `${c.label} = ${c.rank}`)).join('\n');

function CardFace({ card }: { card: Card }) {
  return (
    <span className="inline-flex items-center gap-1" data-testid="card-face">
      {card.picture && <HandoutPicture id={card.picture} alt={card.label} />}
      <span>{card.label}</span>
    </span>
  );
}

function DeckForm({ start, onDone }: { start: UserDeck | null; onDone: (saved: boolean) => void }) {
  const { setNotice } = useTableState();
  const [name, setName] = useState(start?.name ?? '');
  const [lines, setLines] = useState(start ? linesFromCards(start.cards) : '');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const got = await saveDeck({ name: name.trim(), cards: cardsFromLines(lines) }, start?.id ?? null);
    if (got.kind === 'ok') { setNotice(start ? 'Deck saved.' : 'Deck written.'); onDone(true); }
    else setError(got.kind === 'refused' ? got.reason : "That deck couldn't be saved just now.");
  };
  return (
    <form className="space-y-2 rounded border border-stone-700 p-2" aria-label={start ? 'Edit deck' : 'New deck'} onSubmit={(e) => { e.preventDefault(); void save(); }}>
      <label className="block text-xs">Name<input className={input} value={name} maxLength={CARD_LIMITS.name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="block text-xs">Cards, one a line (add "= 5" for a rank used by initiative)
        <textarea className={`${input} h-32`} value={lines} onChange={(e) => setLines(e.target.value)} />
      </label>
      {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
      <div className="flex gap-1">
        <button type="submit" className={btn} disabled={!name.trim() || !lines.trim()}>{start ? 'Save deck' : 'Write deck'}</button>
        <button type="button" className={btn} onClick={() => onDone(false)}>Cancel</button>
      </div>
    </form>
  );
}

/** The DM's own decks and the standard one: write, edit, delete, put on the table. */
function DeckLibrary() {
  const { campaignId } = useShell();
  const { setNotice } = useTableState();
  const [decks, setDecks] = useState<UserDeck[] | null>(null);
  const [editing, setEditing] = useState<UserDeck | 'new' | null>(null);
  const [jokers, setJokers] = useState(false);
  const load = useCallback(async () => { const got = await myDecks(); setDecks(got.kind === 'ok' ? got.value.decks : []); }, []);
  useEffect(() => { void load(); }, [load]);
  const put = async (from: { mine: string } | { standard: { jokers: boolean } }) => {
    const got = await placeDeck(campaignId, from);
    setNotice(got.kind === 'ok' ? `${got.value.name} is on the table, shuffled.` : got.kind === 'refused' ? got.reason : "That deck couldn't be put on the table just now.");
  };
  return (
    <section className="space-y-2" aria-label="Your decks">
      <h3 className="vtt-section-head">Your decks</h3>
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={btn} onClick={() => void put({ standard: { jokers } })}>Add the standard deck</button>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={jokers} onChange={(e) => setJokers(e.target.checked)} />with two jokers</label>
        <button type="button" className={btn} onClick={() => setEditing('new')}>New deck</button>
      </div>
      {editing && <DeckForm start={editing === 'new' ? null : editing} onDone={(saved) => { setEditing(null); if (saved) void load(); }} />}
      {decks?.length === 0 && <p className="text-xs text-stone-400">No decks of your own yet.</p>}
      <ul className="space-y-1">
        {decks?.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-1 text-sm">
            <span className="grow">{d.name} <span className="text-xs text-stone-400">({cardCount(d.cards.length)})</span></span>
            <button type="button" className={btn} onClick={() => void put({ mine: d.id })} aria-label={`Put ${d.name} on the table`}>Put on table</button>
            <button type="button" className={btn} onClick={() => setEditing(d)} aria-label={`Edit ${d.name}`}>Edit</button>
            <button type="button" className={btn} onClick={() => void deleteDeck(d.id).then(load)} aria-label={`Delete ${d.name}`}>Delete</button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function InPlay({ deck, dm }: { deck: SeenDeck; dm: boolean }) {
  const { act, setNotice, store, meId } = useTableState();
  const { table } = useShell();
  const people = table.members.filter((m) => m.role !== 'watcher');
  const nameOf = (id: string) => { const m = table.members.find((x) => x.id === id); return m ? memberLabel(m, store.state.objects as never, {}) : 'Someone'; };
  const others = people.filter((p) => p.id !== meId);
  const [passTo, setPassTo] = useState(others[0]?.id ?? '');
  const [dealTo, setDealTo] = useState(people.find((p) => p.role === 'player')?.id ?? '');
  const said = (ok: string) => (r: { ok: boolean }) => { if (r.ok) setNotice(ok); };
  return (
    <section className="space-y-2 rounded border border-stone-700 p-2" aria-label={`Deck ${deck.name}`} data-testid="deck-in-play">
      <h3 className="vtt-title">{deck.name}</h3>
      <p className="text-xs" data-testid="draw-pile">Draw pile: {cardCount(deck.draw)}</p>
      <p className="text-xs" data-testid="discard-pile">Discard pile: {cardCount(deck.discard.count)}{deck.discard.top && <> — top: <CardFace card={deck.discard.top} /></>}</p>
      <div className="flex flex-wrap gap-1">
        <button type="button" className={btn} disabled={deck.draw === 0} onClick={() => act({ kind: 'deck.draw', deck: deck.id, count: 1 }, said('You drew a card.'))}>Draw a card</button>
        {dm && (
          <>
            <select className="rounded border border-stone-700 bg-stone-900 text-xs" aria-label="Deal to" value={dealTo} onChange={(e) => setDealTo(e.target.value)}>
              {people.map((p) => <option key={p.id} value={p.id}>{nameOf(p.id)}</option>)}
            </select>
            <button type="button" className={btn} disabled={deck.draw === 0 || !dealTo} onClick={() => act({ kind: 'deck.draw', deck: deck.id, count: 1, to: dealTo }, said(`Dealt a card to ${nameOf(dealTo)}.`))}>Deal</button>
            <button type="button" className={btn} disabled={deck.draw === 0} onClick={() => act({ kind: 'deck.draw', deck: deck.id, count: 1, to: 'table' }, said('Dealt a card face up.'))}>Deal face up</button>
            <button type="button" className={btn} onClick={() => act({ kind: 'deck.shuffle', deck: deck.id }, said('Shuffled: the discards and the table went back in.'))}>Shuffle</button>
            <button type="button" className={btn} onClick={() => act({ kind: 'encounter.deal', deck: deck.id }, said('Initiative dealt: the turn order follows the cards.'))}>Deal initiative</button>
            <button type="button" className={btn} onClick={() => act({ kind: 'deck.remove', deck: deck.id }, said(`${deck.name} is off the table.`))}>Remove</button>
          </>
        )}
      </div>
      {deck.table.length > 0 && (
        <div>
          <h4 className="vtt-section-head">Face up on the table</h4>
          <ul className="text-sm" data-testid="table-cards">
            {deck.table.map((c) => (
              <li key={c.id} className="flex items-center gap-1"><CardFace card={c} />
                {dm && <button type="button" className={btn} onClick={() => act({ kind: 'card.discard', deck: deck.id, card: c.id })} aria-label={`Discard ${c.label}`}>Discard</button>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <ul className="text-xs" aria-label="Other hands">
        {Object.entries(deck.hands).map(([who, n]) => <li key={who} data-testid="hand-count">{nameOf(who)}: {cardCount(n)}</li>)}
      </ul>
      <div>
        <h4 className="vtt-section-head">Your hand ({cardCount(deck.mine.length)})</h4>
        {others.length > 0 && deck.mine.length > 0 && (
          <label className="block text-xs">Pass to <select className="rounded border border-stone-700 bg-stone-900" value={passTo} onChange={(e) => setPassTo(e.target.value)}>
            {others.map((p) => <option key={p.id} value={p.id}>{nameOf(p.id)}</option>)}
          </select></label>
        )}
        <ul className="space-y-1 text-sm" data-testid="my-hand">
          {deck.mine.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-1">
              <span className="grow"><CardFace card={c} /></span>
              <button type="button" className={btn} onClick={() => act({ kind: 'card.play', deck: deck.id, card: c.id })} aria-label={`Play ${c.label}`}>Play</button>
              <button type="button" className={btn} onClick={() => act({ kind: 'card.discard', deck: deck.id, card: c.id })} aria-label={`Discard ${c.label}`}>Discard</button>
              <button type="button" className={btn} disabled={!passTo} onClick={() => act({ kind: 'card.pass', deck: deck.id, card: c.id, to: passTo }, said(`Passed ${c.label} to ${nameOf(passTo)}.`))} aria-label={`Pass ${c.label}`}>Pass</button>
              <button type="button" className={btn} onClick={() => act({ kind: 'card.reveal', deck: deck.id, card: c.id }, said(`You showed ${c.label}.`))} aria-label={`Show ${c.label}`}>Show</button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function CardsPanel() {
  const { decks, viewingAs } = useTableState();
  // `notes`'s (issues 1159, 1160): the deck controls follow the power, whichever board a co-DM sees.
  const dm = useMayAct()('deck.add');
  const inPlay = Object.values(decks);
  // View as… gives this panel no decks (`TableState`): the DM was sent their own hand and everyone else's as a count,
  // so the viewed person's hand is not in this client to show, and a deck without it would not be their view. Say so,
  // rather than "No deck is on the table." over a table that may have one.
  if (viewingAs) {
    const who = viewingAs.label ?? (viewingAs.handle ? `@${viewingAs.handle}` : 'this person');
    return (
      <div className="flex h-full flex-col gap-3 overflow-y-auto p-2 text-sm" data-testid="cards">
        <p className="text-xs text-stone-400" data-testid="cards-viewing-as">Cards aren't shown while you view the table as {who}: their hand is theirs alone. The decks are back when you go back to your view.</p>
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-2 text-sm" data-testid="cards">
      {dm && <DeckLibrary />}
      {inPlay.length === 0 && <p className="text-xs text-stone-400">No deck is on the table.</p>}
      {inPlay.length === 0 && !dm && <p className="text-xs text-stone-400" data-testid="cards-empty-hint">The DM puts one out; the cards you are dealt appear here.</p>}
      {inPlay.map((d) => <InPlay key={d.id} deck={d} dm={dm} />)}
    </div>
  );
}
